import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const LeaguesContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Tournaments are great for one-off competitions, but sometimes you want something that runs
      over weeks or months — a season where every result matters. Today we're launching Leagues,
      the most requested feature since Tournaly launched.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      What are Leagues?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      A League is a container for multiple tournaments that share the same participants and
      accumulate points over time. Think Formula 1, a football league season, or your
      office gaming championship — everyone plays, every match counts, and a final standings
      table crowns the overall winner.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Key features
    </Text>
    {[
      'Create a league and add participants once — they carry across all tournaments in the league',
      'Choose a point system: F1 style, linear, winner-takes-all, or fully custom',
      'Add as many tournaments as you want to a league, in any bracket format',
      'Live leaderboard aggregates points from all completed tournaments automatically',
      'Public leagues can be explored and followed by other players',
      'Free plan: 1 active league. Pro plan: unlimited leagues',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      How to get started
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Head to the Leagues section in the navigation drawer, tap "Create League", give it a name
      and pick your scoring system. Add your participants, then create your first tournament
      inside the league. Standings update automatically as matches are played.
    </Text>
  </View>
);

const LeaguesContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Los torneos son geniales para competiciones puntuales, pero a veces quieres algo que dure
      semanas o meses — una temporada donde cada resultado importa. Hoy lanzamos las Ligas,
      la feature más solicitada desde que Tournaly existe.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      ¿Qué son las Ligas?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Una Liga es un contenedor de varios torneos que comparten los mismos participantes y
      acumulan puntos a lo largo del tiempo. Piensa en la Fórmula 1, una liga de fútbol o el
      campeonato de gaming de tu oficina — todos juegan, cada partido cuenta, y una tabla
      final de clasificación corona al ganador global.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Características principales
    </Text>
    {[
      'Crea una liga y añade participantes una vez — se mantienen en todos los torneos de la liga',
      'Elige un sistema de puntos: estilo F1, lineal, el ganador se lo lleva todo, o completamente personalizado',
      'Añade todos los torneos que quieras a una liga, en cualquier formato de bracket',
      'El marcador en directo agrega puntos de todos los torneos completados automáticamente',
      'Las ligas públicas pueden ser exploradas y seguidas por otros jugadores',
      'Plan gratuito: 1 liga activa. Plan Pro: ligas ilimitadas',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Cómo empezar
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Ve a la sección Ligas en el menú de navegación, pulsa "Crear Liga", ponle un nombre
      y elige tu sistema de puntuación. Añade tus participantes y crea tu primer torneo
      dentro de la liga. La clasificación se actualiza automáticamente conforme se juegan los partidos.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-02-08-leagues',
  title: 'Introducing Leagues: Run a Full Season',
  titleEs: 'Presentamos las Ligas: Organiza una Temporada Completa',
  date: '2026-02-08',
  summary: 'Run multi-tournament seasons with cumulative standings using Tournaly Leagues — the tournament league management tool for clubs, gaming communities, and sports organizations.',
  summaryEs: 'Gestiona temporadas multi-torneo con clasificación acumulada en Tournaly Leagues — la herramienta de gestión de ligas para clubs, comunidades de gaming y organizaciones deportivas.',
  tags: ['Leagues', 'Feature'],
  Content: LeaguesContent,
  ContentEs: LeaguesContentEs,
};
